/**
 * Hover card of a palette sample: where it sits on the marked palette plate,
 * its measured color and width, the sample's design-space parameters (power
 * and pulse duration in the laser's own units) and its marking time per unit
 * cluster (1 mm long, the selection time filter's metric) and per cm² filled
 * at its cluster width.
 *
 * Shared by the reproduction's Edit colors step and the selection overview's
 * palette, so a palette color reads the same wherever it is picked.
 */
import React from "react";
import { Box, Divider } from "@mui/material";

const present = (value) => value !== null && value !== undefined;
const trimmed = (value, digits = 4) => String(Number(Number(value).toFixed(digits)));

// Label/value row and section title of the hover card - the layout of the
// plate views' patch tooltip (ExplorationPlates PatchTooltip).
function TooltipRow({ label, value }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
      <Box component="span" sx={{ opacity: 0.7 }}>{label}</Box>
      <Box component="span" sx={{ fontVariantNumeric: "tabular-nums" }}>{value}</Box>
    </Box>
  );
}

function TooltipSection({ children }) {
  return <Box sx={{ fontWeight: 600, opacity: 0.85, mt: 0.75 }}>{children}</Box>;
}

/**
 * `entry`: sample_id, index, hex, l_star/a_star/b_star, width_mm, design,
 * time_per_unit_cluster_s, time_per_cm2_s and, when the plate is a grid,
 * row/column. `note` replaces the plate position in the header (e.g. the
 * capture mode of the reading shown).
 */
export default function PaletteSampleTooltip({ entry, note = null }) {
  const design = entry.design || {};
  const designRows = [
    ["power", design.power],
    ["speed (mm/s)", design.speed],
    ["frequency (kHz)", present(design.frequency) ? design.frequency / 1000 : null],
    ["pulse duration", design.pulse_duration],
    ["hatching (mm)", design.hatching],
    ["lines", design.line_count],
    ["passes", design.pass_count],
  ].filter(([, value]) => present(value));
  const hasTime = present(entry.time_per_unit_cluster_s) || present(entry.time_per_cm2_s);
  const width = Number(entry.width_mm);
  const headerNote = note ?? (present(entry.row) ? `Row ${entry.row} · Col ${entry.column}` : null);
  return (
    <Box sx={{ fontSize: 11, lineHeight: 1.5, minWidth: 220 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}>
        <Box
          sx={{
            width: 12, height: 12, borderRadius: 0.5, bgcolor: entry.hex, flexShrink: 0,
            border: "1px solid rgba(255, 255, 255, 0.35)",
          }}
        />
        <Box sx={{ fontWeight: 700, flex: 1 }}>
          {present(entry.index) ? `Palette color ${entry.index}` : "Palette sample"}
        </Box>
        {headerNote ? <Box sx={{ opacity: 0.7 }}>{headerNote}</Box> : null}
      </Box>

      <TooltipSection>Performance</TooltipSection>
      <TooltipRow
        label="L*a*b*"
        value={`${Number(entry.l_star).toFixed(1)}, ${Number(entry.a_star).toFixed(1)}, ${Number(entry.b_star).toFixed(1)}`}
      />
      <TooltipRow label="width (mm)" value={width > 0 ? width.toFixed(3) : "–"} />

      {designRows.length ? (
        <>
          <TooltipSection>Design</TooltipSection>
          {designRows.map(([label, value]) => (
            <TooltipRow key={label} label={label} value={trimmed(value)} />
          ))}
        </>
      ) : null}

      {hasTime ? (
        <>
          <TooltipSection>Marking time</TooltipSection>
          {present(entry.time_per_unit_cluster_s) ? (
            <TooltipRow label="per unit cluster (s)" value={trimmed(entry.time_per_unit_cluster_s, 3)} />
          ) : null}
          {present(entry.time_per_cm2_s) ? (
            <TooltipRow label="per cm² (s)" value={trimmed(entry.time_per_cm2_s, 1)} />
          ) : null}
        </>
      ) : null}

      <Divider sx={{ my: 0.75, borderColor: "rgba(255,255,255,0.25)" }} />
      <Box sx={{ fontFamily: "monospace", fontSize: 9.5, opacity: 0.8, wordBreak: "break-all" }}>
        {`sample ${entry.sample_id}`}
      </Box>
    </Box>
  );
}

/** The overview's sample row (api/selection/overview) as a tooltip entry. */
export function overviewSampleEntry(sample, hex) {
  return {
    sample_id: sample.id,
    index: sample.palette_index,
    row: sample.palette_row,
    column: sample.palette_column,
    hex,
    l_star: sample.l_star,
    a_star: sample.a_star,
    b_star: sample.b_star,
    width_mm: sample.width_mm,
    design: sample.design,
    time_per_unit_cluster_s: sample.time_per_unit_cluster,
    time_per_cm2_s: sample.time_per_cm2_s,
  };
}
